<template>
    <v-container fluid class="admin-page">
        <!-- Header -->
        <div class="page-header">
            <div>
                <p class="page-kicker">Administration</p>
                <h1 class="page-title">Utilisateurs</h1>
            </div>

            <v-sheet class="page-actions" color="transparent">
                <v-text-field
                    v-model="pattern"
                    v-on:keyup.enter="initialize"
                    prepend-inner-icon="mdi-magnify"
                    label="Recherche"
                    placeholder="Nom, email, matricule..."
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="search-field"
                />
                <v-btn theme="dark" color="#2563eb" @click="initialize" class="action-button">
                    <v-icon>mdi-cloud-sync-outline</v-icon> Recharger
                </v-btn>
                <v-btn theme="dark" color="#059669" @click="createUser()" class="action-button">
                    <v-icon>mdi-plus-circle-outline</v-icon> Nouvel utilisateur
                </v-btn>
            </v-sheet>
        </div>

        <!-- Loader -->
        <v-skeleton-loader v-if="isLoading" type="table"></v-skeleton-loader>

        <!-- Data -->
        <v-table density="compact" v-if="!isLoading && users.length" class="modern-table">
            <template v-slot:default>
                <thead>
                    <tr>
                        <th class="text-left">Nom complet</th>
                        <th class="text-left">Matricule</th>
                        <th class="text-left">Structure</th>
                        <th class="text-left">Profile</th>
                        <th class="text-left" v-if="canAccess">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in users" :key="item.id" :style="{ cursor: 'pointer' }">
                        <td>
                            <div class="d-flex flex-row">
                                <v-avatar size="30" class="my-1 mr-2">
                                    <v-img src="/img/user.png"></v-img>
                                </v-avatar>
                                <div class="align-self-center">
                                    <div class="font-weight-medium">
                                        {{ `${item.prenom} ${item.nom}` }}
                                    </div>
                                    <div class="font-weight-light">
                                        {{ item.email }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td>{{ item.matricule }}</td>
                        <td>{{ getEtablissementLabel(item) }}</td>
                        <td>{{ item.role }}</td>
                        <td v-if="canAccess">
                            <v-btn icon variant="text" color="#2563eb" @click="editUser(item)" class="row-action">
                                <v-icon>mdi-pencil-outline</v-icon>
                            </v-btn>
                            <v-btn
                                icon
                                variant="text"
                                color="red"
                                v-if="canBeDeleted(item)"
                                @click="showDialog(item)"
                                class="row-action"
                            >
                                <v-icon>mdi-trash-can</v-icon>
                            </v-btn>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-table>

        <!-- Pagination -->
        <v-pagination
            class="mt-5"
            v-if="!isLoading && pagination.total_page > 1"
            v-model="pagination.page"
            :length="pagination.total_page"
            :total-visible="6"
            @update:model-value="initialize"
        ></v-pagination>

        <!-- Empty Data -->
        <empty-data v-if="!isLoading && !users.length" text="Désolé, pas de données" />
        <v-dialog v-model="dialog" width="auto">
            <user-delete :userId="userToDelete" @cancel="dialog = false" @onSuccess="afterDelete"></user-delete>
        </v-dialog>
    </v-container>
</template>

<script lang="js">
import UserDelete from '../../../components/User/UserDelete.vue';
import EmptyData from '../../../components/shared/EmptyData.vue';
import profile from "../../../functions/profiles";
export default {
    name: 'UserIndex',
    components: { UserDelete, EmptyData },
    data: () => ({
        users: [],
        pattern: '',
        isLoading: false,
        perPage: 8,
        pagination: {
            page: 1,
            total_page: 1
        },
        userToDelete: '',
        dialog: false
    }),
    computed: {
        canAccess() {
            return profile.PROFILE_SUPERADMIN === this.$store.state.auth.user.role;
        }
    },
    methods: {
        initialize() {
            this.isLoading = true;
            axios.get(`/users`, {
                params: {
                    pattern: this.pattern,
                    page: this.pagination.page,
                    perPage: this.perPage
                }
            })
                .then((response) => {
                    const payload = response && response.data ? response.data : response;
                    const meta = payload && payload.meta ? payload.meta : payload;

                    this.users = Array.isArray(payload?.data) ? payload.data : [];
                    this.pagination.page = Number(meta?.current_page || this.pagination.page || 1);
                    this.pagination.total_page = Number(meta?.last_page || 1);
                })
                .catch(e => {
                    this.users = [];
                    this.pagination.page = 1;
                    this.pagination.total_page = 1;
                    this.$swal.fire('Failed', `errors: ${e}`, 'error')
                })
                .finally(() => {
                    this.isLoading = false;
                 });
        },
        editUser(item) {
            this.$router.push({
                name: 'UserEdit',
                params: { userId: item.id }
            });
        },
        showDialog(user) {
            this.userToDelete = user.id;
            this.dialog = true;
        },
        showDelete(user) {
            return user.id !== this.$store.state.auth.user.id;
        },
        canBeDeleted(user) {
            return user.role !== this.$store.state.auth.user.role;
        },
        getEtablissementLabel(user) {
            if (user && user.etablissement_libelle) {
                return user.etablissement_libelle;
            }

            if (user && user.etablissement && user.etablissement.nom) {
                return user.etablissement.nom;
            }

            return 'Non renseigné';
        },
        afterDelete() {
            this.dialog = false;
            this.initialize();
        },
        createUser() {
            this.$router.push({ name: 'UserCreate' })
        },
    },

    created() {
        this.initialize();
    },

}
</script>
<style scoped>
.admin-page {
    max-width: 100%;
}
.page-header {
    align-items: center;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    margin-bottom: 12px;
}
.page-kicker {
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 0 0 4px;
    text-transform: uppercase;
}
.page-title {
    color: #0f172a;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: 0;
    line-height: 1.2;
    margin: 0;
}
.page-actions {
    align-items: center;
    display: flex;
    gap: 12px;
    min-width: min(720px, 100%);
}
.search-field {
    flex: 1 1 300px;
}
.action-button {
    border-radius: 8px;
    box-shadow: none;
    height: 36px;
    text-transform: none;
}
.modern-table {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
}
.row-action {
    margin-right: 4px;
}
@media (max-width: 900px) {
    .page-header {
        align-items: stretch;
        flex-direction: column;
    }
    .page-actions {
        align-items: stretch;
        flex-direction: column;
        min-width: 0;
        width: 100%;
    }
}
</style>
